import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { AnnouncementsService } from './announcements.service';
import { AnnouncementHubService } from './announcement-hub.service';
import { AnnouncementAttention } from '../models/announcement.models';
import { OAuthService } from '../../../shared/services/oauth.service';
import { LoggerService } from '../../../shared/services/logger.service';

const EMPTY_ATTENTION: AnnouncementAttention = { attentionCount: 0, bannerCount: 0, blocking: [] };

/**
 * Owns the announcement "attention" summary for the whole application: what needs the
 * bell, what needs the arrival banner, and what needs the blocking dialog.
 *
 * Both the summary and the SignalR connection used to belong to the home-page widget.
 * That meant anyone who worked from a bookmarked deep link — `/worktime` being the
 * common one — never fetched anything and never opened the hub, so an announcement
 * could sit unread indefinitely without a single hint anywhere in the UI. Both now
 * live here and are driven by the login state rather than by any component happening
 * to be on screen.
 *
 * Injected by the app shell (header, banner, gate), so it is constructed at startup.
 */
@Injectable({ providedIn: 'root' })
export class AnnouncementNotificationService implements OnDestroy {
  private readonly attentionSubject = new BehaviorSubject<AnnouncementAttention>(EMPTY_ATTENTION);
  /**
   * Emits once the refresh triggered by a hub arrival has landed. Deliberately a plain
   * Subject, not derived from attentionSubject: the gate needs to tell "a new
   * announcement just arrived while someone was mid-session" apart from "the app just
   * fetched attention on login/startup" — the two call for different dialog timing.
   */
  private readonly arrivalsSubject = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  readonly attention$ = this.attentionSubject.asObservable();
  readonly attentionCount$ = this.attention$.pipe(map(a => a.attentionCount), distinctUntilChanged());
  readonly bannerCount$ = this.attention$.pipe(map(a => a.bannerCount), distinctUntilChanged());
  readonly blocking$ = this.attention$.pipe(map(a => a.blocking));
  readonly arrivals$ = this.arrivalsSubject.asObservable();

  get attention(): AnnouncementAttention {
    return this.attentionSubject.value;
  }

  get attentionCount(): number {
    return this.attentionSubject.value.attentionCount;
  }

  constructor(
    private readonly announcementsService: AnnouncementsService,
    private readonly hub: AnnouncementHubService,
    private readonly oauthService: OAuthService,
    private readonly logger: LoggerService
  ) {
    // Subscribed once, not per login: the hub only emits while connected, and
    // re-subscribing on every login would stack duplicate handlers.
    this.hub.announcementReceived$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refresh(true));

    this.oauthService.loginChanged
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(loggedIn => (loggedIn ? this.start() : this.stop()));
  }

  /**
   * Re-reads the attention summary from the API. Safe to call repeatedly.
   *
   * @param isArrival Set by the hub subscription only: marks this refresh as the one
   * that followed a live SignalR arrival, so `arrivals$` fires once it lands. Other
   * callers (login, or a consumer such as the gate dialog re-syncing after an
   * acknowledge) leave it false so they never trigger the deferred-dialog timing.
   */
  refresh(isArrival = false): void {
    this.announcementsService.getAttention()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: attention => {
          this.attentionSubject.next(attention);
          if (isArrival) {
            this.arrivalsSubject.next();
          }
        },
        error: error => this.logger.errorWithContext(
          'AnnouncementNotificationService', 'Failed to load the attention summary', error)
      });
  }

  private start(): void {
    this.refresh();
    this.hub.startConnection().catch(error =>
      this.logger.errorWithContext(
        'AnnouncementNotificationService', 'Failed to start the announcement hub', error));
  }

  private stop(): void {
    // Clear before disconnecting so a logged-out shell cannot show a stale badge.
    this.attentionSubject.next(EMPTY_ATTENTION);
    this.hub.stopConnection().catch(error =>
      this.logger.errorWithContext(
        'AnnouncementNotificationService', 'Failed to stop the announcement hub', error));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
